import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, children, testid, badge }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6" data-testid={testid}>
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-head text-3xl sm:text-4xl font-black tracking-tighter">{title}</h1>
          {badge && <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-primary" data-testid="page-badge"><span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />{badge}</span>}
        </div>
        {subtitle && <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

const TONES = {
  default: { text: "text-foreground", stripe: "bg-slate-400", ring: "bg-slate-100 text-slate-600" },
  primary: { text: "text-primary", stripe: "bg-primary", ring: "bg-primary/10 text-primary" },
  danger: { text: "text-destructive", stripe: "bg-destructive", ring: "bg-red-50 text-red-600" },
  success: { text: "text-[#16A34A]", stripe: "bg-[#16A34A]", ring: "bg-emerald-50 text-emerald-600" },
  blue: { text: "text-secondary", stripe: "bg-secondary", ring: "bg-secondary/10 text-secondary" },
  warning: { text: "text-amber-600", stripe: "bg-amber-500", ring: "bg-amber-50 text-amber-600" },
};

export function StatCard({ label, value, icon: Icon, tone = "default", sub, testid, onClick, active, size = "md" }) {
  const t = TONES[tone] || TONES.default;
  const Tag = onClick ? "button" : "div";
  const sm = size === "sm";
  return (
    <Tag onClick={onClick} className={cn("relative overflow-hidden bg-card border rounded-xl text-left hover-lift w-full", sm ? "p-3 pl-4" : "p-5 pl-6", onClick && "cursor-pointer", active && "ring-2 ring-primary/40")} data-testid={testid || "stat-card"}>
      <span className={cn("absolute left-0 top-3 bottom-3 w-1 rounded-r", t.stripe)} />
      <div className="flex items-center justify-between gap-2">
        <span className={cn("font-bold uppercase tracking-[0.18em] text-muted-foreground font-head", sm ? "text-[10px]" : "text-[11px]")}>{label}</span>
        {Icon && <span className={cn("rounded-lg flex items-center justify-center shrink-0", sm ? "h-6 w-6" : "h-8 w-8", t.ring)}><Icon className={sm ? "h-3.5 w-3.5" : "h-4 w-4"} /></span>}
      </div>
      <div className={cn("font-head font-black tabular-nums tracking-tight leading-none", sm ? "mt-1 text-xl" : "mt-2 text-2xl sm:text-[28px]", t.text)}>
        {value}
      </div>
      {sub && <div className={cn("text-muted-foreground", sm ? "text-[11px] mt-1" : "text-xs mt-1.5")}>{sub}</div>}
    </Tag>
  );
}

export function Empty({ title = "Kayıt bulunamadı", desc, action }) {
  return (
    <div className="text-center py-16 text-muted-foreground border border-dashed rounded-xl bg-card/50">
      <p className="font-semibold text-foreground/80">{title}</p>
      {desc && <p className="text-sm mt-1">{desc}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

// Bölüm kartı: başlık şeridi + içerik (raporlar, tablolar, grafikler)
export function Panel({ title, subtitle, icon: Icon, children, className = "", testid, actions, id }) {
  return (
    <section id={id} className={cn("bg-card border rounded-xl shadow-sm", id && "scroll-mt-32", className)} data-testid={testid}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 px-5 py-3.5 border-b">
          <div className="flex items-center gap-2 min-w-0">
            {Icon && <Icon className="h-4 w-4 text-primary shrink-0" />}
            <div className="min-w-0">
              <h3 className="font-head text-sm font-bold uppercase tracking-wider text-slate-600 truncate">{title}</h3>
              {subtitle && <p className="text-[11px] text-muted-foreground truncate">{subtitle}</p>}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

// Kart satırı hücresi: küçük büyük-harf etiket + değer (e-İrsaliye liste tasarımı)
export const Cell = ({ label, children, className = "" }) => (
  <div className={`min-w-0 ${className}`}><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">{label}</div>{children}</div>
);

// Segment (pill) sekme grubu
export function Segmented({ value, onChange, options, testid, size = "sm" }) {
  return (
    <div className={cn("inline-flex gap-1 bg-muted/60 rounded-lg p-1", size === "xs" && "p-0.5")} data-testid={testid}>
      {options.map(([k, l, count]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={testid ? `${testid}-${k}` : undefined}
          className={cn("px-3 py-1.5 rounded-md text-sm font-semibold transition-colors inline-flex items-center gap-1.5", size === "xs" && "px-2 py-1 text-xs",
            value === k ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")}>
          {l}{count != null && <span className={cn("text-[10px] tabular-nums rounded-full px-1.5 py-px", value === k ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>{count}</span>}
        </button>
      ))}
    </div>
  );
}
